'use client'

import { useEffect, useState } from 'react'
import clsx from 'clsx'
import type { ParseKeys } from 'i18next'
import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { setI18nNamespace, tKey } from '@/utils/i18n'
import { Text } from 'ui/src/components/Text/Text'
import { yStack } from 'ui/src/patterns/stack'
import { type FlowProps } from '../flows.types'
import { ChatInputBar } from './ChatInputBar'
import {
  bubble,
  bubbleUser,
  header,
  inputBar,
  inputBarSvg,
  messageRowBot,
  messageRowUser,
  messages,
  typingDot,
  typingRow,
} from './Flow02Chat.css'

setI18nNamespace('appShowcase')

type AppShowcaseKey = ParseKeys<'appShowcase'>

type ChatMessage = {
  id: number
  type: 'user' | 'bot'
  textKey: AppShowcaseKey
}

const MESSAGES: Array<ChatMessage> = [
  {
    id: 0,
    type: 'user',
    textKey: tKey<'appShowcase'>('FLOW_02_MESSAGE_1'),
  },
  { id: 1, type: 'bot', textKey: tKey<'appShowcase'>('FLOW_02_MESSAGE_2') },
  { id: 2, type: 'user', textKey: tKey<'appShowcase'>('FLOW_02_MESSAGE_3') },
  {
    id: 3,
    type: 'bot',
    textKey: tKey<'appShowcase'>('FLOW_02_MESSAGE_4'),
  },
  { id: 4, type: 'user', textKey: tKey<'appShowcase'>('FLOW_02_MESSAGE_5') },
]

const spring = {
  type: 'spring' as const,
  stiffness: 400,
  damping: 30,
  mass: 0.8,
}

// The conversation choreography: each step is a snapshot of what is on
// screen, held for durationMs before advancing to the next step.
type ScriptStep = {
  visibleMessages: number
  isTyping: boolean
  durationMs: number
}

const SCRIPT: Array<ScriptStep> = [
  { visibleMessages: 0, isTyping: false, durationMs: 500 },
  { visibleMessages: 1, isTyping: false, durationMs: 500 },
  { visibleMessages: 1, isTyping: true, durationMs: 1500 },
  { visibleMessages: 2, isTyping: false, durationMs: 1000 },
  { visibleMessages: 3, isTyping: false, durationMs: 500 },
  { visibleMessages: 3, isTyping: true, durationMs: 2000 },
  { visibleMessages: 4, isTyping: false, durationMs: 1000 },
  { visibleMessages: 5, isTyping: false, durationMs: 0 },
]

export function Flow02Chat(_props: FlowProps) {
  const { t } = useTranslation('appShowcase')
  const [stepIndex, setStepIndex] = useState(0)
  const { visibleMessages, isTyping } = SCRIPT[stepIndex]

  useEffect(() => {
    if (stepIndex === SCRIPT.length - 1) return
    const timeout = setTimeout(() => setStepIndex((index) => index + 1), SCRIPT[stepIndex].durationMs)
    return () => clearTimeout(timeout)
  }, [stepIndex])

  return (
    <div className={yStack({ height: '100%' })}>
      <div className={header}>
        <Text as="span" size="sm" color="textPrimary">
          {t('FLOW_02_HEADER')}
        </Text>
      </div>

      <div className={messages}>
        {MESSAGES.slice(0, visibleMessages).map((message) => {
          const isUser = message.type === 'user'

          return (
            <motion.div
              key={message.id}
              className={isUser ? messageRowUser : messageRowBot}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={spring}
            >
              <Text as="div" className={clsx(bubble, isUser && bubbleUser)} size={{ _: 'xxs', md: 'xs' }}>
                {t(message.textKey)}
              </Text>
            </motion.div>
          )
        })}

        {isTyping && (
          <motion.div
            className={typingRow}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={spring}
          >
            {[0, 1, 2].map((dot) => (
              <motion.div
                key={dot}
                className={typingDot}
                animate={{ opacity: [0.07, 0.8, 0.07] }}
                transition={{ duration: 1, repeat: Infinity, delay: dot * 0.2 }}
              />
            ))}
          </motion.div>
        )}
      </div>

      <div className={inputBar}>
        <ChatInputBar className={inputBarSvg} />
      </div>
    </div>
  )
}
